import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import GetAssignments from "../../../actions/course/GetAssignments";
import SetPage from "../../../data/SetPage";
import AssignmentSubmit from "./AssignentSubmit";
import DetectDevice from "../../../data/DetectDevice";
import ENDPOINT from "../../../data/ENDPOINT";
import SecurityHeaders from "../../../data/SecurityHeaders";

// MUI Components & Icons
import { Avatar, Skeleton, Box, Modal, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EventIcon from "@mui/icons-material/Event";
import StarsIcon from "@mui/icons-material/Stars";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Lock } from "@material-ui/icons";
import PendingActionsIcon from "@mui/icons-material/PendingActions"; // For Total Assignments
import CloudUploadIcon from "@mui/icons-material/CloudUpload"; // For Submitted
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"; // For Completed
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"; // For Rejected
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// External Libraries
import axios from "axios";
import dateFormat from "dateformat";
import styled from "styled-components";
import ModalStyle from "../../../data/ModalStyle";

// Assuming you have an SVG for no assignments. If not, replace with a simple message.
import noassignments from "../../../assets/images/no-assignments.svg";

const MyAssignments = ({ account }) => {
  const params = useParams();
  const course_url = params.course_url;
  const batch_id = params.batch_id;
  const { user } = useContext(AuthContext);

  const [assignments, setAssignments] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentTask, setCurrentTask] = useState(null);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [showDescriptions, setShowDescriptions] = useState({});

  const fetchAssignments = async () => {
    setLoading(true);
    const res = await GetAssignments({
      payload: {
        user: user.SessionId,
        course_url: course_url,
        batch_id: batch_id,
      },
    });
    if (res && res.status === "success" && res.data !== null) {
      setAssignments(res.data);
      setReport(res.report[0]);
      setInitialData(res.data);
      setFilteredAssignments(res.data);
      // Initialize showDescriptions for each assignment
      const initialDescState = {};
      res.data.forEach((_, index) => {
        initialDescState[index] = false;
      });
      setShowDescriptions(initialDescState);
    }
    setLoading(false);
  };

  useEffect(() => {
    SetPage("Assignments");
    window.scrollTo(0, 0);
    fetchAssignments();
  }, []);

  const initiateSubmission = (id) => {
    setCurrentTask(id);
    setSubmitModalOpen(true);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filteredData = initialData.filter((item) =>
      item.title.toLowerCase().includes(term),
    );
    setFilteredAssignments(filteredData);
  };

  const toggleDescription = (index) => {
    setShowDescriptions((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Calculate batch progress percentage
  const batchProgress =
    report && report.total_assignments > 0
      ? Math.round(
          ((report.completed ? report.completed : 0) /
            report.total_assignments) *
            100,
        )
      : 0;
  // Responsive style for stat cards: 2 per row on mobile
  const mobileStatCardStyle = `
  @media (max-width: 600px) {
    .premium-stats-row {
      display: flex !important;
      flex-wrap: wrap !important;
      flex-direction: row !important;
      justify-content: center !important;
    }
    .premium-stats-row .premium-stat-card {
      flex: 1 1 45%;
      max-width: 48%;
      margin: 8px 1% !important;
      min-width: 140px !important;
    }
  }
`;
  return (
    <StyledAssignmentPage>
      <style>{mobileStatCardStyle}</style>
      {loading ? (
        <div className="container py-5">
          <Skeleton variant="rectangular" height={80} className="mb-4" />
          <Skeleton variant="rectangular" height={120} className="mb-5" />
          <div className="row">
            {[...Array(6)].map((_, i) => (
              <div className="col-lg-4 col-md-6 mb-4" key={i}>
                <Skeleton
                  variant="rectangular"
                  height={250}
                  style={{ borderRadius: "12px" }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <Modal
            open={submitModalOpen}
            onClose={() => setSubmitModalOpen(false)}
            aria-labelledby="submit-assignment-modal-title"
          >
            <Box
              sx={ModalStyle}
              className="col-lg-4 col-md-5 col-sm-6 col-11 rounded p-3"
            >
              <div className="modal-header">
                <h4>Upload Assignment</h4>
                <CloseIcon
                  className="click"
                  onClick={() => setSubmitModalOpen(false)}
                />
              </div>
              <hr />
              <AssignmentSubmit
                id={currentTask}
                HandleClose={() => setSubmitModalOpen(false)}
                Refresh={fetchAssignments}
              />
            </Box>
          </Modal>

          <div className="container py-4 py-md-5">
            <div className="page-header mb-4 d-flex flex-wrap justify-content-between align-items-end border-bottom pb-3 col-lg-12 col-md-12 col-sm-12 col-12">
              <div className="page-header">
                <div className="d-flex align-items-center mb-2">
                  <img
                    src="https://api.iconify.design/heroicons-solid:document-text.svg?color=%237c3aed"
                    alt="My Assignments"
                    style={{ width: "38px", height: "38px", marginRight: 10 }}
                  />
                  <h2
                    style={{
                      fontWeight: 800,
                      fontSize: "2.1rem",
                      background:
                        "linear-gradient(90deg, #040404 0%, #383838 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      margin: 0,
                    }}
                  >
                    My Assignments
                  </h2>
                </div>
                <p
                  style={{
                    color: "#404245",
                    fontSize: "1.08rem",
                    margin: 0,
                    marginBottom: 18,
                  }}
                >
                  Track your progress and submit your work on time
                </p>
              </div>

              <div className="header-actions mt-3 ">
                <div
                  className="search-box"
                  style={{ position: "relative", width: "100%", marginTop: 8 }}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search assignments by title..."
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{
                      paddingLeft: "40px",
                      borderRadius: "12px",
                      border: "2px solid #e38f28",
                      fontSize: "1.13rem",
                      background: "#fff",
                      boxShadow: "0 2px 8px rgba(124,58,237,0.07)",
                      height: 48,
                      outline: "none",
                      color: "#222",
                    }}
                  />
                  <SearchIcon
                    style={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#7c3aed",
                      fontSize: "1.5rem",
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mb-4">
              <Link
                to={`/batch-progress/${course_url}/${batch_id}`}
                className="btn btn-primary-custom d-flex align-items-center"
                style={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  padding: "0.5rem 1.2rem",
                  background: "linear-gradient(90deg, #f68139, #ff9960)",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  fontSize: "1rem",
                  transition: "background 0.2s",
                }}
              >
                <TrendingUpIcon style={{ marginRight: 8 }} />
                View Batch Progress
              </Link>
            </div>

            {/* --- Premium Stats Row (Dashboard) --- */}
            {report && (
              <div
                className="premium-stats-row mb-5"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: "16px 0",
                }}
              >
                <style>{`
                  @media (max-width: 600px) {
                    .premium-stats-row {
                      display: flex !important;
                      flex-wrap: wrap !important;
                      flex-direction: row !important;
                      justify-content: center !important;
                    }
                    .premium-stats-row .premium-stat-card {
                      flex: 1 1 45%;
                      max-width: 48%;
                      margin: 8px 1%;
                    }
                  }
                `}</style>
                <div
                  className="premium-stat-card total"
                  style={{
                    background:
                      "linear-gradient(135deg, #e0f2ff 0%, #d2eaff 100%)",
                    border: "2px solid #3b82f6",
                    minWidth: 180,
                    maxWidth: 220,
                    margin: "0 12px",
                  }}
                >
                  <PendingActionsIcon
                    className="stat-icon"
                    style={{ color: "#3b82f6" }}
                  />
                  <div className="stat-label">Total</div>
                  <div className="stat-value">{report.total_assignments}</div>
                </div>
                <div
                  className="premium-stat-card sent"
                  style={{
                    background:
                      "linear-gradient(135deg, #e0fff4 0%, #d2ffe7 100%)",
                    border: "2px solid #22c55e",
                    minWidth: 180,
                    maxWidth: 220,
                    margin: "0 12px",
                  }}
                >
                  <CloudUploadIcon
                    className="stat-icon"
                    style={{ color: "#22c55e" }}
                  />
                  <div className="stat-label">Submitted</div>
                  <div className="stat-value">{report.submitted}</div>
                </div>
                <div
                  className="premium-stat-card done"
                  style={{
                    background:
                      "linear-gradient(135deg, #fffbe0 0%, #fff7d2 100%)",
                    border: "2px solid #eab308",
                    minWidth: 180,
                    maxWidth: 220,
                    margin: "0 12px",
                  }}
                >
                  <AssignmentTurnedInIcon
                    className="stat-icon"
                    style={{ color: "#eab308" }}
                  />
                  <div className="stat-label">Completed</div>
                  <div className="stat-value">
                    {report.completed ? report.completed : 0}
                  </div>
                </div>
                <div
                  className="premium-stat-card failed"
                  style={{
                    background:
                      "linear-gradient(135deg, #ffe0e0 0%, #ffd2d2 100%)",
                    border: "2px solid #ef4444",
                    minWidth: 180,
                    maxWidth: 220,
                    margin: "0 12px",
                  }}
                >
                  <ErrorOutlineIcon
                    className="stat-icon"
                    style={{ color: "#ef4444" }}
                  />
                  <div className="stat-label">Rejected</div>
                  <div className="stat-value">
                    {report.rejected ? report.rejected : 0}
                  </div>
                </div>
              </div>
            )}

            {/* --- Premium Assignments Row --- */}
            <div className="premium-assignments-row">
              {filteredAssignments.length > 0 ? (
                filteredAssignments.map((project, index) => (
                  <div
                    key={index}
                    className="premium-assignment-card"
                    style={{
                      // border: "2px solid #e5e7eb",
                      // borderTop: "6px solid #f68139",
                      boxShadow: "0 8px 32px rgba(80,120,200,0.10)",
                      position: "relative",
                    }}
                  >
                    <div className="premium-card-top">
                      <div
                        className="premium-badge"
                        style={{ background: "#f3e8ff", color: "#a21caf" }}
                      >
                        {project.category || "Design"}
                      </div>
                      <div
                        className="premium-points"
                        style={{ color: "#a21caf" }}
                      >
                        <StarsIcon
                          style={{ fontSize: "1.2rem", marginRight: 2 }}
                        />
                        {project.marks}pts
                      </div>
                    </div>
                    <div
                      className="premium-title"
                      style={{ color: "#7c3aed", fontWeight: 700 }}
                    >
                      {project.title}
                    </div>
                    <div
                      className="premium-meta-row"
                      style={{
                        // display: "flex",
                        // alignItems: "center",
                        gap: "0.7rem",
                        marginBottom: "1.1rem",
                      }}
                    >
                      <div
                        className="premium-meta"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <EventIcon
                          style={{
                            fontSize: "1.rem",
                            marginRight: 4,
                            color: "#64748b",
                          }}
                        />
                        <span style={{ color: "#64748b", fontSize: "15px" }}>
                          {dateFormat(
                            project.deadline,
                            "mmm dd, yyyy • hh:MM TT",
                          )}
                        </span>
                      </div>
                      {/* View Leaderboard button right of date */}
                      {project.lock_status !== "locked" ? (
                        <Link
                          to={
                            project.lock_status !== "locked" &&
                            `/assignment-leaderboard/${course_url}/${project.assignment_id}`
                          }
                          rel="noopener noreferrer"
                          className="px-3 py-2 rounded-pill badge bg-info"
                          style={{
                            background: "#ede9fe",
                            color: "#7c3aed",
                            fontWeight: 550,
                            marginLeft: 10,
                            fontSize: "0.95rem",
                          }}
                        >
                          View Leaderboard
                        </Link>
                      ) : (
                        <span
                          className="px-3 py-2 rounded-pill badge1 badge"
                          style={{
                            background: "#f3f4f6",
                            color: "#a1a1aa",
                            fontWeight: 600,
                            marginLeft: 10,
                            fontSize: "0.98rem",
                          }}
                        >
                          Leaderboard Locked
                        </span>
                      )}{" "}
                      <div
                        className={`premium-status-dot ${project.status === "expired" ? "expired" : ""}`}
                        style={{
                          background:
                            project.status === "expired"
                              ? "#ef4444"
                              : "#10b981",
                        }}
                      />
                      <div
                        className={`premium-status-label ${project.status === "expired" ? "expired" : ""}`}
                        style={{
                          color:
                            project.status === "expired"
                              ? "#ef4444"
                              : "#10b981",
                        }}
                      >
                        {project.status === "expired" ? "EXPIRED" : ""}
                      </div>
                    </div>
                    {/* --- Assignment Details, Description, and Buttons --- */}
                    {project.lock_status !== "locked" && (
                      <div
                        className="description-toggle mt-2 mb-2"
                        onClick={() => toggleDescription(index)}
                      >
                        <span className="description-text">
                          Assignment Details
                        </span>
                        {showDescriptions[index] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </div>
                    )}
                    <div
                      className={`ass-description ${showDescriptions[index] ? "show" : ""}`}
                    >
                      <div className="description-content">
                        <p
                          dangerouslySetInnerHTML={{
                            __html: project.description,
                          }}
                        ></p>
                      </div>
                    </div>
                    {/* <div
                      className="card-footer p-3 px-4"
                      style={{
                        background: "transparent",
                        border: "none",
                        boxShadow: "none",
                        padding: 0,
                      }}
                    >
                      {project.status === "submitted" ? (
                        <button disabled className="btn-status btn-submitted">
                          <CheckCircleIcon />
                          <span>Submitted</span>
                        </button>
                      ) : project.status === "completed" ? (
                        <button disabled className="btn-status btn-submitted">
                          <CheckCircleIcon />
                          <span>Completed</span>
                        </button>
                      ) : project.lock_status === "locked" ? (
                        <button disabled className="btn-status btn-locked">
                          <Lock /> Locked
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            initiateSubmission(project.assignment_id)
                          }
                          className="btn-status btn-submit"
                          style={{
                            background: "#7c3aed",
                            color: "#fff",
                            fontWeight: 700,
                          }}
                        >
                          Submit Assignment
                        </button>
                      )}
                    </div> */}
                    <div className="premium-card-bottom">
                      {project.status === "submitted" ? (
                        <button
                          disabled
                          className="premium-btn submitted"
                          style={{
                            background: "#22c55e",
                            color: "#fff",
                            width: "70%",
                            fontWeight: 700,
                          }}
                        >
                          <CheckCircleIcon style={{ marginRight: 6 }} />{" "}
                          Submitted
                        </button>
                      ) : project.status === "completed" ? (
                        <button
                          disabled
                          className="premium-btn submitted"
                          style={{
                            background: "#eab308",
                            color: "#fff",
                            width: "70%",
                            fontWeight: 700,
                          }}
                        >
                          <CheckCircleIcon style={{ marginRight: 6 }} />{" "}
                          Completed
                        </button>
                      ) : project.lock_status === "locked" ? (
                        <button
                          disabled
                          className="premium-btn locked"
                          style={{
                            background: "#e5e7eb",
                            color: "#64748b",
                            width: "70%",
                            fontWeight: 700,
                          }}
                        >
                          <Lock style={{ marginRight: 6 }} /> Locked
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            initiateSubmission(project.assignment_id)
                          }
                          className="premium-btn"
                          style={{
                            background:
                              "linear-gradient(90deg, #f68139, #ff9960)",
                            color: "#fff",
                            width: "70%",
                            fontWeight: 700,
                            border: "none",
                            boxShadow: "0 2px 12px rgba(246,129,57,0.13)",
                            borderRadius: "16px",
                            transition: "background 0.2s, box-shadow 0.2s",
                          }}
                        >
                          Submit Assignment
                        </button>
                      )}
                      <div
                        className="premium-trophy"
                        style={{
                          background: "#fff7ed",
                          border: "2px solid #f68139",
                          boxShadow: "0 2px 8px rgba(246,129,57,0.08)",
                          marginLeft: 16,
                          padding: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                        }}
                      >
                        <StarsIcon
                          style={{ color: "#f68139", fontSize: "1.5rem" }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="premium-no-assignments">
                  <img src={noassignments} alt="No assignments found" />
                  <h4>No Assignments Found</h4>
                  <p className="text-muted">
                    There are no assignments matching your search.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </StyledAssignmentPage>
  );
};

export default MyAssignments;

const StyledAssignmentPage = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5ff 100%);
  min-height: 100vh;
  font-family: "Inter", sans-serif;
  color: #222;
  .dashboard-loader {
    max-width: 600px;
    margin: 0 auto;
    padding: 2.5rem 0;
  }
  .premium-container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 2.5rem 1rem;
  }
  .premium-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    margin-bottom: 2.5rem;
    .premium-header-left {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      width: 100%;
      .premium-search {
        flex: 1;
        border: none;
        outline: none;
        font-size: 1.2rem;
        background: #f7f7fa;
        border-radius: 16px;
        padding: 1.1rem 1.5rem;
        box-shadow: 0 2px 12px rgba(80, 120, 200, 0.08);
        margin-right: 1.2rem;
        min-width: 220px;
        max-width: 350px;
        transition: box-shadow 0.2s;
      }
      .premium-search:focus {
        box-shadow: 0 4px 24px rgba(80, 120, 200, 0.16);
      }
      .premium-progress-btn {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        background: #181c3a;
        color: #fff;
        border-radius: 2rem;
        padding: 0.7rem 2.2rem;
        font-weight: 700;
        border: none;
        font-size: 1.1rem;
        text-decoration: none;
        box-shadow: 0 2px 12px rgba(80, 120, 200, 0.08);
        transition:
          background 0.2s,
          box-shadow 0.2s;
        &:hover {
          background: #7c3aed;
          color: #fff;
          box-shadow: 0 4px 24px rgba(124, 58, 237, 0.12);
        }
      }
    }
  }
  .premium-stats-row {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
    flex-wrap: wrap;
    justify-content: flex-start;
    .premium-stat-card {
      flex: 1 1 120px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: #fff;
      border-radius: 22px;
      box-shadow: 0 4px 24px rgba(80, 120, 200, 0.1);
      padding: 1.5rem 1.2rem 1.2rem 1.2rem;
      min-width: 120px;
      max-width: 160px;
      transition:
        box-shadow 0.2s,
        transform 0.2s;
      cursor: pointer;
      border: none;
      .stat-icon {
        font-size: 2.2rem;
        margin-bottom: 0.7rem;
      }
      .stat-label {
        font-size: 1.05rem;
        font-weight: 700;
        color: #7b849b;
        margin-bottom: 0.2rem;
        letter-spacing: 0.04em;
      }
      .stat-value {
        font-size: 2.1rem;
        font-weight: 800;
        color: #181c3a;
      }
    }
    .premium-stat-card.total {
      background: linear-gradient(135deg, #e0e7ff 0%, #f1f5ff 100%);
      .stat-icon {
        color: #6366f1;
      }
      .stat-value {
        color: #181c3a;
      }
    }
    .premium-stat-card.sent {
      background: linear-gradient(135deg, #e0f7ef 0%, #e6fff7 100%);
      .stat-icon {
        color: #10b981;
      }
      .stat-value {
        color: #181c3a;
      }
    }
    .premium-stat-card.done {
      background: linear-gradient(135deg, #fff7e0 0%, #fffbe6 100%);
      .stat-icon {
        color: #f59e42;
      }
      .stat-value {
        color: #181c3a;
      }
    }
    .premium-stat-card.failed {
      background: linear-gradient(135deg, #ffe0e0 0%, #fff1f1 100%);
      .stat-icon {
        color: #ef4444;
      }
      .stat-value {
        color: #181c3a;
      }
    }
    .premium-stat-card:hover {
      box-shadow: 0 8px 32px rgba(124, 58, 237, 0.13);
      transform: translateY(-4px) scale(1.04);
    }
  }
  .premium-assignments-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 2.2rem;
  }
  .premium-assignment-card {
    background: #fff;
    border-radius: 28px;
    box-shadow: 0 8px 32px rgba(80, 120, 200, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 240px;
    border: none;
    padding: 2rem 2rem 1.5rem 2rem;
    position: relative;
    transition:
      box-shadow 0.2s,
      transform 0.2s;
    &:hover {
      box-shadow: 0 12px 40px rgba(124, 58, 237, 0.13);
      transform: translateY(-6px) scale(1.03);
    }
    .premium-card-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.7rem;
      .premium-badge {
        background: #f3f4f6;
        color: #7b849b;
        font-size: 1rem;
        font-weight: 700;
        border-radius: 12px;
        padding: 0.3rem 1.1rem;
        letter-spacing: 0.04em;
        margin-right: 0.7rem;
      }
      .premium-points {
        color: #181c3a;
        font-size: 1.1rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 0.2rem;
      }
    }
    .premium-title {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 0.7rem;
      letter-spacing: 0.01em;
    }
    .premium-meta-row {
      display: flex;
      align-items: center;
      gap: 0.7rem;
      margin-bottom: 1.1rem;
      .premium-meta {
        color: #7b849b;
        font-size: 1rem;
        display: flex;
        align-items: center;
      }
      .premium-status-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #10b981;
        &.expired {
          background: #ef4444;
        }
      }
      .premium-status-label {
        font-size: 1rem;
        font-weight: 700;
        color: #10b981;
        &.expired {
          color: #ef4444;
        }
      }
    }
    .premium-card-bottom {
      display: flex;
      align-items: center;
      margin-top: 1.2rem;
      .premium-btn {
        border: none;
        border-radius: 16px;
        font-weight: 700;
        font-size: 1.1rem;
        padding: 0.9rem 2.2rem;
        margin-right: 1.2rem;
        background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
        color: #fff;
        box-shadow: 0 2px 12px rgba(16, 185, 129, 0.08);
        display: flex;
        align-items: center;
        gap: 0.7rem;
        transition:
          background 0.2s,
          box-shadow 0.2s;
        cursor: pointer;
        &:hover {
          background: linear-gradient(90deg, #34d399 0%, #10b981 100%);
          box-shadow: 0 4px 24px rgba(16, 185, 129, 0.13);
        }
        &.locked {
          background: #e5e7eb;
          color: #7b849b;
          cursor: not-allowed;
        }
        &.submitted {
          background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
          color: #fff;
          cursor: not-allowed;
        }
      }
      .premium-trophy {
        background: #f7f7fa;
        border-radius: 50%;
        padding: 0.3rem;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(80, 120, 200, 0.08);
        margin-left: auto;
      }
    }
  }
  .premium-no-assignments {
    text-align: center;
    margin-top: 3.5rem;
    img {
      width: 180px;
      margin-bottom: 1.2rem;
    }
    h4 {
      font-weight: 700;
      margin-bottom: 0.7rem;
      color: #2a3a5c;
    }
    p {
      color: #6c757d;
      font-size: 1.1rem;
    }
  }
  .premium-modal {
    width: 97vw;
    max-width: 480px;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 8px 32px rgba(80, 120, 200, 0.12);
    padding: 2.5rem 2rem;
    backdrop-filter: blur(8px);
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
      margin: 0;
      color: #2a3a5c;
      font-weight: 800;
    }
    .click {
      cursor: pointer;
      color: #6c757d;
      font-size: 1.4rem;
    }
  }
  @media (max-width: 1100px) {
    .premium-container {
      max-width: 100%;
      padding: 1.2rem 0.5rem;
    }
    .premium-header {
      flex-direction: column;
      gap: 1.2rem;
      .premium-header-left {
        flex-direction: column;
        gap: 0.9rem;
        .premium-search {
          width: 100%;
        }
        .premium-progress-btn {
          width: 100%;
          justify-content: center;
        }
      }
    }
    .premium-stats-row {
      flex-direction: column;
      gap: 0.9rem;
    }
    .premium-assignments-row {
      grid-template-columns: 1fr;
      gap: 1.2rem;
    }
    .premium-assignment-card {
      min-width: 0;
    }
  }
  @media (max-width: 600px) {
    .premium-modal {
      padding: 1.2rem 0.5rem;
      border-radius: 12px;
    }
    .premium-header-left .premium-search {
      font-size: 1rem;
      padding: 0.8rem 1rem;
    }
    .premium-assignment-card {
      padding: 1.2rem 0.7rem 1rem 0.7rem;
      border-radius: 14px;
    }
    .premium-card-bottom .premium-btn {
      font-size: 1rem;
      padding: 0.7rem 1.2rem;
      border-radius: 10px;
    }
    .premium-meta-row {
      flex-wrap: wrap;
    }
  }
  @media (max-width: 400px) {
    .premium-header-left .premium-progress-btn {
      font-size: 0.95rem;
    }
    .premium-meta-row {
      flex-wrap: wrap;
    }
  }
`;